import { StockData, StockFactors, PillarScores, computeFactors, scoreStocks } from './compute';
import { IncomeStatement, BalanceSheet, CashFlow } from './fmp';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface VintageStockData {
  symbol: string;
  sector: string;
  pillarScores: PillarScores;
  valuationScore: number | null;
  qualityScore: number | null;
  factorValues: Partial<Record<keyof StockFactors, number | null>>;
  forwardReturn: number | null;
}

export interface BacktestOutput {
  updatedAt: string;
  vintages: string[];
  factorICs: Record<string, Record<string, number | null>>;
  pillarICs: Record<string, Record<string, number | null>>;
  compositeICs: Record<string, number | null>;
  avgFactorICs: Record<string, number | null>;
  avgPillarICs: Record<string, number | null>;
  avgCompositeIC: number | null;
  quintileReturns: Record<string, number[]>;
  avgQuintileReturns: number[];
  vintageStocks: Record<string, VintageStockData[]>;
  stockCount: number;
  // Optional 6x6 grid data (from backtest.ts refresh script)
  benchmarkReturn?: number | null;
  gridMedians?: (number | null)[][];
  gridCounts?: number[][];
  minReturn?: number;
  maxReturn?: number;
  // Multi-period grid data
  periods?: Record<string, {
    gridMedians: (number | null)[][];
    gridCounts: number[][];
    quadrantMedians: Record<string, { median: number | null; count: number; label: string }>;
    minReturn: number;
    maxReturn: number;
    benchmark: number;
    stockCount: number;
  }>;
  // RSI overlay backtest — populated by scripts/backtest-rsi.ts (kept loose; consumed only in BacktestView.tsx).
  rsiBacktest?: Record<string, unknown>;
}

export interface PricePoint { date: string; close: number }

// ─── Spearman Rank Correlation ───────────────────────────────────────────────

function rank(arr: number[]): number[] {
  const indexed = arr.map((v, i) => ({ v, i }));
  indexed.sort((a, b) => a.v - b.v);
  const ranks = new Array(arr.length);
  let i = 0;
  while (i < indexed.length) {
    let j = i;
    while (j < indexed.length && indexed[j].v === indexed[i].v) j++;
    const avgRank = (i + j - 1) / 2 + 1;
    for (let k = i; k < j; k++) ranks[indexed[k].i] = avgRank;
    i = j;
  }
  return ranks;
}

export function spearmanCorrelation(x: number[], y: number[]): number | null {
  if (x.length !== y.length || x.length < 10) return null;
  const rx = rank(x);
  const ry = rank(y);
  const n = x.length;
  const meanX = rx.reduce((a, b) => a + b, 0) / n;
  const meanY = ry.reduce((a, b) => a + b, 0) / n;
  let num = 0, denX = 0, denY = 0;
  for (let i = 0; i < n; i++) {
    const dx = rx[i] - meanX;
    const dy = ry[i] - meanY;
    num += dx * dy;
    denX += dx * dx;
    denY += dy * dy;
  }
  const den = Math.sqrt(denX * denY);
  return den === 0 ? null : num / den;
}

// ─── Time-Shift Financials ───────────────────────────────────────────────────

function findStatementsByYear<T extends { date: string }>(stmts: T[], vintageYear: number, count: number): T[] | null {
  // Sort by date descending (most recent first)
  const sorted = [...stmts].sort((a, b) => b.date.localeCompare(a.date));

  // Find the first statement from vintageYear or earlier
  const startIdx = sorted.findIndex(s => {
    const yr = new Date(s.date).getFullYear();
    return yr <= vintageYear;
  });

  if (startIdx < 0) return null;
  const result = sorted.slice(startIdx, startIdx + count);
  return result.length >= 2 ? result : null; // Need at least 2 years
}

export function timeShiftStock(
  stock: StockData,
  vintageYear: number,
  historicalPrice: number,
  historicalMktCap: number,
): StockData | null {
  const income = findStatementsByYear(stock.income, vintageYear, 6);
  const balance = findStatementsByYear(stock.balance, vintageYear, 6);
  const cashflow = findStatementsByYear(stock.cashflow, vintageYear, 6);

  if (!income || !balance || !cashflow) return null;

  return {
    symbol: stock.symbol,
    name: stock.name,
    sector: stock.sector,
    industry: stock.industry,
    marketCap: historicalMktCap,
    price: historicalPrice,
    beta: stock.beta,
    income: income as IncomeStatement[],
    balance: balance as BalanceSheet[],
    cashflow: cashflow as CashFlow[],
    estimates: [], // No forward estimates for historical vintages
  };
}

// ─── Price Lookup ────────────────────────────────────────────────────────────

export function getHistoricalPrice(prices: PricePoint[], targetDate: string): number | null {
  if (prices.length === 0) return null;

  // Find closest price on or after target date (within 10 days)
  const target = new Date(targetDate).getTime();
  let best: PricePoint | null = null;
  let bestDist = Infinity;

  for (const p of prices) {
    const d = new Date(p.date).getTime();
    const dist = Math.abs(d - target);
    if (dist < bestDist) {
      bestDist = dist;
      best = p;
    }
  }

  // Only accept within 10 trading days (~14 calendar days)
  if (!best || bestDist > 14 * 24 * 60 * 60 * 1000) return null;
  return best.close;
}

// ─── Quintile Computation ────────────────────────────────────────────────────

export function computeQuintiles(data: { score: number; ret: number }[]): number[] {
  const sorted = [...data].sort((a, b) => b.score - a.score); // Q1 = highest score
  const n = sorted.length;
  const qSize = Math.floor(n / 5);
  const quintiles: number[] = [];

  for (let q = 0; q < 5; q++) {
    const start = q * qSize;
    const end = q === 4 ? n : start + qSize;
    const slice = sorted.slice(start, end);
    const avgRet = slice.reduce((s, d) => s + d.ret, 0) / slice.length;
    quintiles.push(avgRet);
  }

  return quintiles;
}

// ─── Full Backtest Runner ────────────────────────────────────────────────────

export function runBacktest(
  stocks: StockData[],
  allPrices: Map<string, PricePoint[]>,
  vintageYears: number[],
): BacktestOutput {
  const vintageStocks: Record<string, VintageStockData[]> = {};
  const quintileReturns: Record<string, number[]> = {};

  // All factor keys for IC computation
  const allFactorKeys: (keyof StockFactors)[] = [
    'roe', 'roa', 'roic', 'grossProfitToAssets', 'grossMargin', 'netMargin', 'cfoToAssets',
    'nim', 'efficiencyRatio',
    'fwdEpsGrowth', 'fwdRevGrowth', 'fwdEpsCagr2yr', 'fwdRevCagr2yr', 'epsCAGR3yr', 'fcfGrowth3yr',
    'grossMarginExpansion3yr', 'opMarginExpansion3yr', 'revenueGrowth3yr',
    'altmanZ', 'piotroski', 'debtToEbitda', 'interestCoverage', 'epsVariability', 'beta', 'currentRatio', 'fcfToDebt',
    'equityToAssets', 'retainedEarningsToAssets',
    'netDebtPaydownYield', 'dividendYield', 'buybackYield',
    'insiderNetBuy',
    'fwdPE', 'fwdPE2yr', 'fwdEvToRevenue', 'pegRatio', 'evToEbitda', 'pToFcf', 'evToGrossProfit',
    'priceToBook',
  ];

  const pillarKeys: (keyof PillarScores)[] = ['profitability', 'growth', 'safety', 'shareholderReturns'];

  // IC storage
  const factorICs: Record<string, Record<string, number | null>> = {};
  const pillarICs: Record<string, Record<string, number | null>> = {};
  const compositeICs: Record<string, number | null> = {};

  for (const fk of allFactorKeys) factorICs[fk] = {};
  for (const pk of pillarKeys) pillarICs[pk] = {};
  pillarICs['valuation'] = {};
  pillarICs['quality'] = {};

  for (const vintageYear of vintageYears) {
    const vintageKey = String(vintageYear);
    console.log(`\nScoring vintage ${vintageYear}...`);

    // Scoring date: ~3 months after fiscal year end to allow filing
    const scoreDate = `${vintageYear + 1}-04-01`;
    const returnEndDate = `${vintageYear + 2}-04-01`;

    // Build time-shifted universe
    const shiftedStocks: (StockData & { forwardReturn: number | null })[] = [];

    for (const stock of stocks) {
      const prices = allPrices.get(stock.symbol);
      if (!prices || prices.length === 0) continue;

      const scorePrice = getHistoricalPrice(prices, scoreDate);
      if (!scorePrice) continue;

      // Approximate historical market cap
      const sharesOut = stock.income[0]?.weightedAverageShsOutDil;
      if (!sharesOut) continue;
      const histMktCap = scorePrice * sharesOut;

      const shifted = timeShiftStock(stock, vintageYear, scorePrice, histMktCap);
      if (!shifted) continue;

      // Forward 1-year return
      const endPrice = getHistoricalPrice(prices, returnEndDate);
      const forwardReturn = endPrice ? (endPrice / scorePrice) - 1 : null;

      shiftedStocks.push({ ...shifted, forwardReturn });
    }

    console.log(`  ${shiftedStocks.length} stocks scored for vintage ${vintageYear}`);

    // Compute factors and score
    const withFactors = shiftedStocks.map(s => ({
      ...s,
      factors: computeFactors(s),
    }));
    const scored = scoreStocks(withFactors);

    // Build vintage stock data (merge with forward returns)
    const vintageData: VintageStockData[] = [];
    for (let i = 0; i < scored.length; i++) {
      const s = scored[i];
      const fwdRet = shiftedStocks[i].forwardReturn;

      const factorValues: Partial<Record<keyof StockFactors, number | null>> = {};
      for (const fk of allFactorKeys) {
        factorValues[fk] = s.factors[fk] as number | null;
      }

      vintageData.push({
        symbol: s.symbol,
        sector: s.sector,
        pillarScores: s.pillarScores,
        valuationScore: s.valuationScore,
        qualityScore: s.qualityScore,
        factorValues,
        forwardReturn: fwdRet,
      });
    }

    vintageStocks[vintageKey] = vintageData;

    // Filter to stocks with forward returns for IC/quintile computation
    const withReturns = vintageData.filter(s => s.forwardReturn !== null);
    console.log(`  ${withReturns.length} stocks with forward returns`);

    // Compute factor ICs
    for (const fk of allFactorKeys) {
      const pairs = withReturns
        .filter(s => s.factorValues[fk] !== null && s.factorValues[fk] !== undefined)
        .map(s => ({ x: s.factorValues[fk] as number, y: s.forwardReturn! }));

      if (pairs.length >= 10) {
        // For inverted factors (lower = better), negate so positive IC = good
        const invertedFactors = new Set(['debtToEbitda', 'epsVariability', 'beta', 'efficiencyRatio', 'fwdPE', 'fwdPE2yr', 'fwdEvToRevenue', 'pegRatio', 'evToEbitda', 'pToFcf', 'evToGrossProfit', 'priceToBook']);
        const sign = invertedFactors.has(fk) ? -1 : 1;
        const ic = spearmanCorrelation(
          pairs.map(p => p.x * sign),
          pairs.map(p => p.y),
        );
        factorICs[fk][vintageKey] = ic;
      } else {
        factorICs[fk][vintageKey] = null;
      }
    }

    // Compute pillar ICs
    for (const pk of pillarKeys) {
      const pairs = withReturns
        .filter(s => s.pillarScores[pk] !== null)
        .map(s => ({ x: s.pillarScores[pk]!, y: s.forwardReturn! }));
      pillarICs[pk][vintageKey] = pairs.length >= 10 ? spearmanCorrelation(pairs.map(p => p.x), pairs.map(p => p.y)) : null;
    }

    // Valuation IC
    const valPairs = withReturns
      .filter(s => s.valuationScore !== null)
      .map(s => ({ x: s.valuationScore!, y: s.forwardReturn! }));
    pillarICs['valuation'][vintageKey] = valPairs.length >= 10 ? spearmanCorrelation(valPairs.map(p => p.x), valPairs.map(p => p.y)) : null;

    // Quality IC
    const qualPairs = withReturns
      .filter(s => s.qualityScore !== null)
      .map(s => ({ x: s.qualityScore!, y: s.forwardReturn! }));
    pillarICs['quality'][vintageKey] = qualPairs.length >= 10 ? spearmanCorrelation(qualPairs.map(p => p.x), qualPairs.map(p => p.y)) : null;

    // Composite IC (quality + valuation average)
    const compPairs = withReturns
      .filter(s => s.qualityScore !== null && s.valuationScore !== null)
      .map(s => ({ x: (s.qualityScore! + s.valuationScore!) / 2, y: s.forwardReturn! }));
    compositeICs[vintageKey] = compPairs.length >= 10 ? spearmanCorrelation(compPairs.map(p => p.x), compPairs.map(p => p.y)) : null;

    // Quintile returns
    const quintileData = withReturns
      .filter(s => s.qualityScore !== null && s.valuationScore !== null)
      .map(s => ({ score: (s.qualityScore! + s.valuationScore!) / 2, ret: s.forwardReturn! }));
    quintileReturns[vintageKey] = quintileData.length >= 25 ? computeQuintiles(quintileData) : [];
  }

  // Compute averages across vintages
  const vintageKeys = vintageYears.map(String);

  const avgFactorICs: Record<string, number | null> = {};
  for (const fk of allFactorKeys) {
    const vals = vintageKeys.map(v => factorICs[fk][v]).filter((v): v is number => v !== null);
    avgFactorICs[fk] = vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  }

  const avgPillarICs: Record<string, number | null> = {};
  for (const pk of [...pillarKeys, 'valuation', 'quality']) {
    const vals = vintageKeys.map(v => pillarICs[pk][v]).filter((v): v is number => v !== null);
    avgPillarICs[pk] = vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  }

  const compVals = vintageKeys.map(v => compositeICs[v]).filter((v): v is number => v !== null);
  const avgCompositeIC = compVals.length > 0 ? compVals.reduce((a, b) => a + b, 0) / compVals.length : null;

  // Average quintile returns
  const qVintages = vintageKeys.filter(v => quintileReturns[v].length === 5);
  const avgQuintileReturns = qVintages.length > 0
    ? [0, 1, 2, 3, 4].map(q => qVintages.reduce((s, v) => s + quintileReturns[v][q], 0) / qVintages.length)
    : [];

  return {
    updatedAt: new Date().toISOString(),
    vintages: vintageKeys,
    factorICs,
    pillarICs,
    compositeICs,
    avgFactorICs,
    avgPillarICs,
    avgCompositeIC,
    quintileReturns,
    avgQuintileReturns,
    vintageStocks,
    stockCount: stocks.length,
  };
}
